import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { wxUtils } from "@features/wx/utils";
import { useJobCardReadingsReport } from "../../../hooks";
import { Camera } from "lucide-react";
import { Checkbox } from "@shared/ui/checkbox";

const createPaginationConfig = (data: any[], page: number, perPage: number) => ({
  currentPage: page,
  totalPages: Math.ceil((data?.length || 0) / perPage),
  totalItems: data?.length || 0,
  itemsPerPage: perPage,
  startItem: (page - 1) * perPage + 1,
  endItem: Math.min(page * perPage, data?.length || 0),
});

const getPaginatedData = (data: any[], page: number, perPage: number) => {
  if (!data || data.length === 0) return [];
  const startIndex = (page - 1) * perPage;
  return data.slice(startIndex, startIndex + perPage);
};

export const ReadingsTab = ({
  jobCard,
  isReport,
  id,
  isHistory,
  isCompleted,
}: {
  jobCard: any;
  isReport?: boolean;
  id?: string;
  isHistory?: boolean;
  isCompleted?: boolean;
}) => {

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const { data: readingsReportData } = useJobCardReadingsReport(id, {
    enabled: !!isReport && !!id,
  });

  const transformedData = useMemo(
    () => jobCard && wxUtils.transformJobCardDetail(jobCard),
    [jobCard]
  );

  const reportReadings = useMemo(() => {
    return (
      readingsReportData?.result?.reading?.map((reading, index) => ({
        readingId: index,
        key: reading.key,
        name: reading.name,
        unit: reading.unit,
        minimum: reading.minimum,
        maximum: reading.maximum,
        range: `${reading.minimum}-${reading.maximum}`,
        reading: reading.readings,
        readingFile: reading.readingFile,
        photoRequired: !!reading.readingFile,
      })) || []
    );
  }, [readingsReportData]);

  const readingsData = isReport
    ? reportReadings
    : transformedData?.readingsData || [];

  const readingsColumns = [
    {
      key: "name",
      header: "Name",
      render: (v: string) => (
        <span className="font-medium text-gray-900">{v}</span>
      ),
    },
    { key: "unit", header: "Unit", render: (v: string) => <span>{v}</span> },
    { key: "range", header: "Range", render: (v: string) => <span>{v}</span> },
    ...(isReport
      ? [
          {
            key: "minimum",
            header: "Minimum",
            render: (value, row) => (
              <span className="text-gray-700">
                {row.range?.split("-")[0] || value || "N/A"}
              </span>
            ),
          },
          {
            key: "maximum",
            header: "Maximum",
            render: (value, row) => (
              <span className="text-gray-700">
                {row.range?.split("-")[1] || value || "N/A"}
              </span>
            ),
          },
          {
            key: "reading",
            header: "Reading",
            render: (value) => (
              <span className="text-gray-700">{value || "0"}</span>
            ),
          },
          {
            key: "photo",
            header: "Photo",
            render: (value, row) => (
              <div className="text-center">
                {row.readingFile ? (
                  <img
                    src={row.readingFile}
                    alt="Reading photo"
                    className="w-8 h-8 object-cover rounded border-2 border-gray-300 mx-auto cursor-pointer"
                    onClick={() => window.open(row.readingFile, "_blank")}
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-100 rounded border-2 border-dashed border-gray-300 flex items-center justify-center mx-auto">
                    <Camera className="h-3 w-3 text-gray-400" />
                  </div>
                )}
              </div>
            ),
          },
          {
            key: "action",
            header: "Action",
            render: () => (
              <div className="text-center">
                <Checkbox defaultChecked={!isCompleted} disabled={isHistory} />
              </div>
            ),
          },
        ]
      : [
          {
            key: "photoRequired",
            header: "Photo Required",
            type: "badge" as const,
            badgeConfig: {
              getClassName: (v: string) =>
                v === "Yes"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800",
            },
          },
        ]),
  ];

  return (
    <GenericTable
      data={getPaginatedData(readingsData, currentPage, itemsPerPage)}
      columns={readingsColumns}
      emptyMessage={`No readings found`}
      rowKey={"readingId"}
      pagination={createPaginationConfig(
        readingsData,
        currentPage,
        itemsPerPage
      )}
      onPageChange={setCurrentPage}
    />
  );
};